import { BoxItem } from 'box-ui-elements/es';
import { JSX } from 'react';
import { Divider, Stack, Typography } from '@common-components';
import { Search as SearchIcon } from '@icons';
import { BoxTemplateLabels, FileType } from 'enums';
import { messages } from 'i18n';
import { demoData } from 'utils';
import { useFileActionsCallbacks, useFilesDisplay } from 'broker/components/FilesExplorer/hooks';
import { ExtendedBoxItem, GetFileSecondaryActions, LabelMetadata } from 'broker/components/FilesExplorer/types';
import { shouldApplySearchInputFilter } from 'broker/components/FilesExplorer/utils';
import FileCard from './FileCard/FileCard';
import FileItemSkeleton from './FileItemSkeleton';

interface FilesListSectionProps {
  // From FilesExplorerProps (passed through from parent)
  selectionMode?: boolean;
  onFileSelected?: (file: ExtendedBoxItem) => void;
  selectedFiles?: ExtendedBoxItem[];
  setSelectedFiles?: (boxItems: ExtendedBoxItem[]) => void;
  getFileSecondaryActions?: GetFileSecondaryActions;
  getPrimaryActionComponent?: (file: ExtendedBoxItem) => JSX.Element;
  highlightFileTypes?: FileType[];
  viewOnlyMode?: boolean;
  labelsMetadata?: LabelMetadata[];
  submissionId?: string;

  // Internal props from Content component
  afterFileOperation: () => Promise<void>;
  silentAfterFileOperation: () => Promise<void>;
  onLabelUpdate: (
    extendedBoxItem: ExtendedBoxItem,
    labelFieldName: BoxTemplateLabels,
    selectedLabel?: string,
  ) => Promise<void>;
  isLoading?: boolean;
  doesFolderHasFiles: boolean;
  uploaderShown: boolean;
  filesSlice: BoxItem[];
  search: string;
}

export default function FilesListSection({
  afterFileOperation,
  isLoading,
  doesFolderHasFiles,
  uploaderShown,
  filesSlice,
  search,
  onLabelUpdate,
  selectionMode,
  onFileSelected,
  selectedFiles,
  getFileSecondaryActions,
  getPrimaryActionComponent,
  highlightFileTypes,
  viewOnlyMode,
  labelsMetadata,
  setSelectedFiles,
  submissionId,
}: FilesListSectionProps) {
  const { onDeleteClicked, onRenameSubmit, onPreviewClicked, onDownloadClicked, onTruncateClicked } =
    useFileActionsCallbacks({
      afterFileOperation,
      selectedFiles,
      setSelectedFiles,
      submissionId,
    });

  const filesDisplay = useFilesDisplay();

  return (
    <>
      {isLoading && (
        <Stack flex={1} gap={4}>
          {demoData(4).map((key) => (
            <FileItemSkeleton key={key.id} />
          ))}
        </Stack>
      )}
      {doesFolderHasFiles &&
        !uploaderShown &&
        (filesSlice.length === 0 && !shouldApplySearchInputFilter(search) ? (
          <Stack flex={1} gap={1} justifyContent="center" alignItems="center">
            <SearchIcon opacity={0.2} fontSize="large" />
            <Typography color="text.disabled" variant="body2">
              {messages.fileExplorer.noItemsFound}
            </Typography>
          </Stack>
        ) : (
          <Stack flex={1} sx={{ overflowY: 'scroll' }} divider={<Divider sx={{ mx: 2 }} />}>
            {filesSlice?.map((file) => (
              <FileCard
                key={file.id}
                file={file}
                searchInput={search}
                onPreviewClicked={onPreviewClicked}
                onRenameSubmit={onRenameSubmit}
                onDownloadClicked={onDownloadClicked}
                onTruncateClicked={onTruncateClicked}
                onDeleteClicked={onDeleteClicked}
                onLabelUpdate={onLabelUpdate}
                selectionMode={selectionMode}
                onSelected={onFileSelected}
                isSelected={
                  selectedFiles && !!selectedFiles.find((currSelectedFile) => currSelectedFile.id === file.id)
                }
                filesDisplay={filesDisplay}
                getFileSecondaryActions={getFileSecondaryActions}
                getPrimaryActionComponent={getPrimaryActionComponent}
                highlightFileTypes={highlightFileTypes}
                showLabelSelector={!viewOnlyMode}
                viewOnlyMode={viewOnlyMode}
                labelsMetadata={labelsMetadata}
              />
            ))}
          </Stack>
        ))}
    </>
  );
}
